import React, { useState, useEffect } from 'react'
import { Building2, Users, UserPlus, Shield, Settings, Crown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/services/api'
import InviteUserModal from './InviteUserModal'

interface Organization {
  organizationId: string
  name: string
  domain: string
  plan: string
  status: string
  ownerUserId: string
}

interface User {
  userId: string
  email: string
  name: string
  role: string
  status: string
  invitedAt: string
}

export default function OrganizationSettings() {
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)

  useEffect(() => {
    loadOrganizationData()
  }, [])

  const loadOrganizationData = async () => {
    try {
      // Get or create user's organization
      const orgResponse = await apiService.request('/api/organizations/demo')
      if (orgResponse.success) {
        const org = orgResponse.data.organization
        setOrganization(org)
        
        // Load organization users
        const usersResponse = await apiService.request(`/api/organizations/${org.organizationId}/users`)
        if (usersResponse.success) {
          setUsers(usersResponse.data.users || [])
        }
      }
    } catch (error) {
      console.error('Failed to load organization data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadge = (role: string) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      manager: 'bg-green-100 text-green-700',
      analyst: 'bg-yellow-100 text-yellow-700',
      viewer: 'bg-gray-100 text-gray-700'
    }
    return colors[role as keyof typeof colors] || colors.viewer
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4" />
      case 'admin': return <Shield className="w-4 h-4" />
      default: return <Users className="w-4 h-4" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Organization Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>Organization Details</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage your organization settings and members
                </p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-700">
              {organization?.plan || 'Enterprise'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Organization Name</p>
              <p className="font-semibold">{organization?.name || 'Loading...'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Domain</p>
              <p className="font-semibold">{organization?.domain || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge className="bg-green-100 text-green-700">{organization?.status || 'Active'}</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Members</p>
              <p className="font-semibold">{users.length} users</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Members */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>Team Members</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage access and permissions for your team
                </p>
              </div>
            </div>
            <Button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Invite User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">No team members yet</p>
              <Button onClick={() => setShowInviteModal(true)} variant="outline">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Your First Member
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <p className="font-semibold">{user.name || user.email}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getRoleBadge(user.role)}>
                      {user.role}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {user.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organizationId={organization?.organizationId || ''}
        onSuccess={loadOrganizationData}
      />
    </div>
  )
}

